import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Page = styled.main`
  min-height: 100vh;
  background: #0b0f14;
  color: #eef2f4;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  line-height: 1.65;
`;

const Container = styled.div`
  width: min(860px, calc(100% - 40px));
  margin: 0 auto;
  padding: 64px 0;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  margin-bottom: 32px;
  color: #8fd7e8;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const Header = styled.header`
  margin-bottom: 36px;
`;

const Eyebrow = styled.p`
  margin: 0 0 10px;
  color: #8fd7e8;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 0 0 12px;
  font-size: clamp(2.25rem, 6vw, 4rem);
  line-height: 1;
  color: #ffffff;
`;

const Lead = styled.p`
  margin: 0;
  max-width: 720px;
  color: #b5c0c6;
  font-size: 1.08rem;
`;

const Panel = styled.section`
  margin: 28px 0;
  padding: 22px;
  background: #121922;
  border: 1px solid #2a3945;
  border-radius: 8px;
`;

const Section = styled.section`
  margin: 32px 0;
`;

const SectionTitle = styled.h2`
  margin: 0 0 12px;
  color: #ffffff;
  font-size: 1.25rem;
`;

const Paragraph = styled.p`
  margin: 0 0 14px;
  color: #b5c0c6;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 1.35rem;
  color: #b5c0c6;

  li {
    margin-bottom: 0.65rem;
  }
`;

const Anchor = styled.a`
  color: #8fd7e8;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const Meta = styled.p`
  margin-top: 42px;
  color: #6f7c84;
  font-size: 0.9rem;
`;

export const EchoSupport: React.FC = () => (
  <Page>
    <Container>
      <BackLink to="/">Back to AI4U</BackLink>
      <Header>
        <Eyebrow>ECHO</Eyebrow>
        <Title>Support</Title>
        <Lead>
          ECHO is an interactive sci-fi narrative about a fragile communication link
          with Lyra Voss, an astronaut stranded on Mars.
        </Lead>
      </Header>

      <Panel>
        <Paragraph>
          If something in the relay breaks, send feedback from inside the app when
          possible. In-app feedback can include session context that helps diagnose
          message delivery, entitlement, or notification issues.
        </Paragraph>
      </Panel>

      <Section>
        <SectionTitle>Common Issues</SectionTitle>
        <List>
          <li>
            Relay access can degrade when the daily communication window is exhausted.
            This is expected narrative behavior, not a permanent account lock.
          </li>
          <li>
            Optional relay passes expand short-term access but do not guarantee a
            specific story outcome.
          </li>
          <li>
            Push notifications require iOS notification permission. If permission is
            denied, new mission updates still appear when the app is opened.
          </li>
          <li>
            Voice input converts speech into text before sending a normal message.
            ECHO does not store an app-owned audio recording.
          </li>
        </List>
      </Section>

      <Section>
        <SectionTitle>Purchases</SectionTitle>
        <Paragraph>
          Purchases are handled by the App Store and RevenueCat. ECHO backend stores
          relay entitlement state so access can expire correctly and be revoked after
          refunds or cancellations.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Privacy</SectionTitle>
        <Paragraph>
          Read the <Anchor href="/echo/privacy/">ECHO Privacy Policy</Anchor> for details
          on chat data, anonymous identifiers, notifications, purchases, and feedback.
        </Paragraph>
      </Section>

      <Meta>Last updated: June 21, 2026</Meta>
    </Container>
  </Page>
);

export const EchoPrivacy: React.FC = () => (
  <Page>
    <Container>
      <BackLink to="/">Back to AI4U</BackLink>
      <Header>
        <Eyebrow>ECHO</Eyebrow>
        <Title>Privacy Policy</Title>
        <Lead>
          This policy describes the data ECHO uses to provide its interactive
          narrative, relay access, feedback, and notifications.
        </Lead>
      </Header>

      <Panel>
        <Paragraph>
          ECHO does not require a named account. The app uses anonymous identifiers
          to keep the story, session security, push delivery, and purchase entitlement
          working across launches.
        </Paragraph>
      </Panel>

      <Section>
        <SectionTitle>Data ECHO Uses</SectionTitle>
        <List>
          <li>Chat messages and generated story replies, used to maintain the conversation with Lyra.</li>
          <li>Anonymous player and session identifiers, used for continuity, session authorization, and relay entitlement.</li>
          <li>Optional feedback submitted from the app, including app version and optional session context.</li>
          <li>APNs device tokens, used only to deliver ECHO notifications after a session exists and notification permission is granted.</li>
          <li>Purchase and entitlement metadata needed to activate, expire, refund, or revoke relay access.</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>Voice Input</SectionTitle>
        <Paragraph>
          Voice input is used to transcribe speech into text while the user holds the
          voice input control. ECHO sends the resulting text message through the normal
          message endpoint. ECHO does not store an app-owned audio recording of voice input.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Purchases</SectionTitle>
        <Paragraph>
          Payments are handled by the App Store and RevenueCat. ECHO backend stores
          relay entitlement state so access can expire correctly and be revoked after
          refunds or cancellations.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Security and Abuse Prevention</SectionTitle>
        <Paragraph>
          ECHO uses session authorization, source-based rate limits, and model usage
          limits to protect the service and control AI model cost. Source identifiers
          are pseudonymized before storage.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Support</SectionTitle>
        <Paragraph>
          For support, use the in-app feedback button or visit{' '}
          <Anchor href="/echo/support/">ECHO Support</Anchor>.
        </Paragraph>
      </Section>

      <Meta>Last updated: June 21, 2026</Meta>
    </Container>
  </Page>
);
